import { motion } from "framer-motion";
import { ArrowRight, Blend, Check, CheckCircle, CreditCard, Globe, Mail, Monitor, Phone, Scissors, ShoppingBag, Smartphone, Utensils } from "lucide-react";
import LeadForm from "@/forms/engine/LeadForm";
import eposDemo from "@/forms/definitions/eposDemo";
import RelatedReviews from "@/components/RelatedReviews";
import LocationCrossLinks from "@/components/LocationCrossLinks";
import FormReviewCompanion from "@/components/FormReviewCompanion";
import Breadcrumbs from "@/components/Breadcrumbs";
import GoogleTrustPill from "@/components/GoogleTrustPill";
import SocialProofStrip from "@/components/SocialProofStrip";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import { useProductViewed } from "@/utils/trackingHooks";
import SEOHead from "@/components/SEOHead";
import { fadeUp, stagger, heroImageMotion, viewportOnce, viewportOnceSmall } from "@/utils/motionVariants";

// Free-setup offer banner — flip to false when the offer ends.
const SHOW_OFFER_BANNER = true;

const OFFER_BANNER_STYLE = { background: "linear-gradient(135deg, #4fa896 0%, #69c0ab 100%)" };

const PAINS = [
  { pain: "A till, a card machine and a spreadsheet that never agree", relief: "One product list, one basket, one report." },
  { pain: "Totals keyed into the card machine by hand", relief: "Payments built in — no mis-keys, faster queues." },
  { pain: "Reports trapped in the till at the shop", relief: "Live sales on any browser, anywhere — free." },
];

const PILLARS = [
  { icon: Monitor, accent: "aqua", title: "EPOS software", body: "A fast, simple till on fixed counters and handhelds. Keeps trading offline, updates free, learns your menu or shelf in minutes." },
  { icon: CreditCard, accent: "aqua", title: "Card payments built in", body: "The PAX A920 takes the payment inside the sale — no separate terminal, no re-keying — on CanDo's transparent pricing, backed by Elavon." },
  { icon: Globe, accent: "pink", title: "Free Web Portal", body: "Products, stock, staff and reports on any browser, in real time — unlimited users, multiple sites, included with every version." },
];

const HARDWARE = [
  { icon: Monitor, accent: "aqua", title: "Fixed countertop till", body: "A full touchscreen till for the counter, with cash drawer and receipt printer." },
  { icon: Smartphone, accent: "aqua", title: "PAX A920 handheld", body: "The whole till and the card machine in one hand — sell at the table, on the floor or in the field." },
  { icon: Blend, accent: "pink", title: "Any mix", body: "Fixed tills and handhelds share one basket, one product list and one report." },
];

const PORTAL_FEATURES = ["Live sales, as they happen", "Products & price changes", "Stock across every site", "Staff & timekeeping", "Unlimited users", "Nothing to install"];

const INDUSTRIES = [
  { icon: Utensils, accent: "aqua", title: "Hospitality", body: "Tabs, split bills and modifiers. Order on the A920 at the table, print straight to the kitchen, pay without leaving the seat." },
  { icon: ShoppingBag, accent: "aqua", title: "Retail", body: "Barcodes, stock control, weighed and measured products, multiple price lists — and a customer-facing display at the counter." },
  { icon: Scissors, accent: "pink", title: "Health & Beauty", body: "Appointment booking with SMS reminders, client records, staff commission reports and timekeeping — alongside the till." },
];

const TIERS = [
  {
    name: "Lite",
    desc: "Get started at the counter.",
    price: "£14.99",
    accent: "aqua",
    cardClass: "border border-brand-light-grey",
    features: ["EPOS essentials, works offline", "Integrated card payments", "Product management", "Sales reports & cash management", "Email receipts, free updates", "Free Web Portal"],
  },
  {
    name: "Core",
    desc: "Everything in Lite, plus stock and scale.",
    price: "£29.99",
    accent: "aqua",
    cardClass: "border-2 border-brand-aqua",
    badge: "Most popular",
    badgeClass: "bg-brand-aqua-dark",
    features: ["Stock management, import/export", "Multiple sites & price lists", "Weighed & measured products", "Extensive reporting", "Customer-facing display", "1 order/receipt printer"],
  },
  {
    name: "Plus",
    desc: "Everything in Core, plus hospitality & service.",
    price: "£39.99",
    accent: "pink",
    cardClass: "border-2 border-brand-pink",
    badge: "The hospitality pick",
    badgeClass: "bg-brand-pink",
    features: ["Tabs, split bills & modifiers", "Unlimited kitchen/order printers", "Appointment booking & SMS reminders", "CRM & customer records", "Commission reports & timekeeping"],
  },
];

const FAQS = [
  { q: "What does \"free setup\" actually include?", a: "The free-setup offer waives the £100 terminal setup fee on every Register plan. Building your system — products, taxes and staff loaded — is a separate one-off cost: £300, or £500 with installation and onsite training." },
  { q: "Are the prices inclusive of VAT?", a: "No — all Register pricing is per till, per month and excludes VAT. Lite is £14.99, Core £29.99 and Plus £39.99 per till, per month, ex VAT." },
  { q: "Can I keep trading if my internet goes down?", a: "Yes. Register keeps trading offline, so you can carry on selling and everything syncs back up when your connection returns." },
  { q: "Do I need a separate card machine?", a: "No — card payments are built in. The PAX A920 takes the payment inside the sale on CanDo's transparent pricing, backed by Elavon, so there's no separate terminal and no re-keying totals." },
  { q: "How quickly can I be up and running?", a: "Days, not weeks. Terminals are delivered next day when you order before 2pm, and we build your system — products, taxes and staff — before you go live." },
  { q: "Which version is right for my business?", a: "Lite gets you started at the counter, Core adds stock management and multi-site scale, and Plus adds hospitality and service tools like tabs, split bills and appointment booking. Not sure which fits? We'll match you in one call." },
  { q: "Does the Web Portal cost extra?", a: "No. Every version of Register includes the free Web Portal — live sales, products, stock, staff and reports on any browser, with unlimited users and multiple sites." },
  { q: "Can I add more tills or handhelds later?", a: "Yes. Extra Core licences are £19.99 a month, extra Plus licences £29.99 a month, and A920 handhelds £15.00 a month — fixed tills and handhelds share one basket, one product list and one report." },
];

const STATS = [
  { figure: "4 min", caption: "Avg. support response — a real person" },
  { figure: "99.9%", caption: "Payment uptime, backed by Elavon" },
  { figure: "Next day", caption: "Terminals delivered — order before 2pm" },
  { figure: "15+ yrs", caption: "Family-run, industry experience" },
];

const STATS_OVERLAY_STYLE = {
  background: "radial-gradient(circle at 20% 50%, #69c0ab 0%, transparent 50%), radial-gradient(circle at 80% 50%, #df65a2 0%, transparent 50%)",
  opacity: 0.06,
};

const PORTAL_CHART_BARS = [42, 58, 38, 66, 52, 78, 60, 88, 70, 96, 82, 64].map((h, i) => ({ h, highlight: i === 9 }));

export default function EposRegisterPage() {
  useProductViewed("epos_register");
  return (
    <div>
      <SEOHead
        title="Register EPOS System — Till, Payments & Back Office"
        description="Register is the EPOS CanDo Payments supplies, sets up and supports — till, built-in card payments and a free web portal, from £14.99 per till per month ex VAT. Book a free demo."
        path="/epos-systems/register"
        service={{ name: "Register EPOS", description: "Register EPOS supplied, set up and supported by CanDo Payments — till software, integrated card payments on the PAX A920 and a free web portal, for UK hospitality, retail and service businesses.", serviceType: "Point of Sale Systems" }}
        faqs={FAQS}
      />

      {/* Offer banner */}
      {SHOW_OFFER_BANNER && (
        <div className="text-white text-sm font-bold text-center py-2.5 px-4 sm:px-8" style={OFFER_BANNER_STYLE} data-testid="register-offer-banner">
          {"Free setup on every Register plan — book a demo and we'll get you trading. "}
          <a href="#contact" className="underline text-white hover:text-white/90" data-testid="register-offer-banner-link">Book now</a>
        </div>
      )}

      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24" data-testid="register-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <Breadcrumbs className="mb-5" />
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-3">Register · Supplied by CanDo Payments</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
                The till, the card machine and the back office. <span className="gradient-text">One system.</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-6 max-w-lg">
                Register is the EPOS we supply, set up and support — running your counter, taking your card payments and reporting to a free web portal wherever you are.
              </motion.p>
              <motion.div variants={fadeUp} className="flex items-baseline gap-2 mb-6" data-testid="register-price-anchor">
                <span className="text-sm font-semibold text-brand-grey">From</span>
                <span className="font-heading text-3xl sm:text-4xl font-black text-brand-black tracking-tight">£14.99</span>
                <span className="text-sm font-semibold text-brand-grey">per till, per month, ex VAT</span>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-8">
                <a href="#contact" className="cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2" data-testid="register-cta-demo">
                  Book a Free Demo <ArrowRight size={16} />
                </a>
                <a href="#versions" className="bg-brand-pink-light text-brand-pink hover:bg-brand-pink/15 border border-brand-pink/30 font-semibold rounded-full px-8 py-3.5 text-sm transition-colors" data-testid="register-cta-versions">
                  Compare Versions
                </a>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-5">
                {["Free setup", "Live in days", "4 min support response"].map((item) => (
                  <span key={item} className="flex items-center gap-2 text-sm font-semibold text-brand-black">
                    <Check size={16} strokeWidth={3} className="text-brand-aqua-dark flex-shrink-0" />{item}
                  </span>
                ))}
              </motion.div>
              <motion.div variants={fadeUp} className="mt-6 flex justify-start">
                <GoogleTrustPill variant="card" testId="register-trust-pill" />
              </motion.div>
            </motion.div>
            <motion.div {...heroImageMotion} className="hidden lg:block">
              <img
                src="https://cdn.candopayments.com/products/epos/smartvolution/sunmi-d3-pro/sunmi-d3-pro-front-cafe_colour_web_transparent.webp"
                alt="Register EPOS running on a Sunmi D3 Pro till"
                width="1600"
                height="1371"
                className="w-full max-w-lg mx-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The problem */}
      <section className="py-20 md:py-28 bg-white" data-testid="register-problem">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="max-w-2xl mb-10">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-3">The problem</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">
              Separate systems make extra work.
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PAINS.map((item) => (
              <motion.div key={item.relief} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <p className="text-brand-pink font-semibold line-through mb-2.5">{item.pain}</p>
                <p className="text-lg font-bold text-brand-black">{item.relief}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What Register is */}
      <section className="pb-20 md:pb-28 bg-white" data-testid="register-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="max-w-2xl mb-10">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-3">What Register is</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">
              Three things, connected.
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${item.accent === "pink" ? "bg-brand-pink-light" : "bg-brand-aqua-light"}`}>
                  <item.icon size={22} className={item.accent === "pink" ? "text-brand-pink" : "text-brand-aqua-dark"} />
                </div>
                <h3 className="font-heading text-xl font-bold text-brand-black mb-3">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Hardware */}
      <section className="py-20 md:py-28 bg-brand-off-white border-y border-brand-light-grey" data-testid="register-hardware">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* TODO: replace with a till + A920 pair photo when one is shot — the fixed till
                is in the hero (Sunmi D3 Pro), the handheld here. */}
            <motion.div {...heroImageMotion} className="order-2 lg:order-1">
              <img
                src="https://cdn.candopayments.com/products/epos/smartvolution/pax-a920-pro/a920pro-angle-cafe_colour_web_transparent.webp"
                alt="PAX A920 Pro handheld running Register"
                width="868"
                height="1219"
                className="w-full max-w-xs mx-auto max-h-[420px] object-contain"
              />
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="order-1 lg:order-2">
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-3">Hardware</motion.p>
              <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight mb-8">
                For the counter — and beyond it.
              </motion.h2>
              <div className="space-y-5">
                {HARDWARE.map((item) => (
                  <motion.div key={item.title} variants={fadeUp} className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.accent === "pink" ? "bg-brand-pink-light" : "bg-brand-aqua-light"}`}>
                      <item.icon size={20} className={item.accent === "pink" ? "text-brand-pink" : "text-brand-aqua-dark"} />
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-bold text-brand-black">{item.title}</h3>
                      <p className="text-sm text-brand-grey leading-relaxed mt-0.5">{item.body}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Free Web Portal */}
      <section className="py-20 md:py-28 bg-white" data-testid="register-portal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-3">Free Web Portal</motion.p>
              <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight mb-4">
                Run the business from anywhere.
              </motion.h2>
              <motion.p variants={fadeUp} className="text-brand-grey leading-relaxed mb-8">
                Every Register comes with the Web Portal at no extra cost — your back office on any browser.
              </motion.p>
              <motion.div variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {PORTAL_FEATURES.map((item) => (
                  <span key={item} className="flex items-center gap-2 text-sm font-semibold text-brand-black">
                    <Check size={16} strokeWidth={3} className="text-brand-aqua-dark flex-shrink-0" />{item}
                  </span>
                ))}
              </motion.div>
            </motion.div>
            {/* TODO: replace this hand-built mockup with a real Web Portal screenshot when available */}
            <motion.div {...heroImageMotion}>
              <div className="bg-brand-off-white border border-brand-light-grey rounded-2xl p-6 sm:p-9" data-testid="register-portal-mockup">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-brand-light-grey">
                  <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-brand-light-grey bg-gray-50">
                    <span className="w-2.5 h-2.5 rounded-full bg-brand-pink-light" />
                    <span className="w-2.5 h-2.5 rounded-full bg-brand-aqua-light" />
                    <span className="w-2.5 h-2.5 rounded-full bg-brand-light-grey" />
                    <span className="ml-3 text-[11px] text-brand-grey font-semibold truncate">portal.register — Dashboard</span>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-3 gap-3 mb-5">
                      {[
                        { label: "Today's sales", value: "£1,284" },
                        { label: "Transactions", value: "142" },
                        { label: "Avg. basket", value: "£9.05" },
                      ].map((stat) => (
                        <div key={stat.label} className="rounded-lg bg-brand-aqua-light/60 px-3 py-2.5">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-brand-aqua-dark">{stat.label}</p>
                          <p className="font-heading text-lg font-black text-brand-black">{stat.value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-end gap-1.5 h-24 mb-5">
                      {PORTAL_CHART_BARS.map((bar) => (
                        <div key={bar.h} className={`flex-1 rounded-t ${bar.highlight ? "bg-brand-pink" : "bg-brand-aqua"}`} style={{ height: `${bar.h}%` }} />
                      ))}
                    </div>
                    <div className="space-y-2">
                      {[
                        ["Flat white ×2", "£6.40"],
                        ["Bacon roll", "£4.95"],
                        ["Table 6 — split bill", "£38.20"],
                      ].map(([label, value]) => (
                        <div key={label} className="flex items-center justify-between text-xs border-b border-brand-light-grey/60 pb-2">
                          <span className="text-brand-grey font-semibold">{label}</span>
                          <span className="text-brand-black font-bold">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 md:py-28 bg-brand-aqua-light" data-testid="register-industries">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="max-w-2xl mb-10">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-3">Industries</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">
              Built for how you trade.
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {INDUSTRIES.map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`register-industry-${item.title.toLowerCase().replace(/\s+&\s+|\s+/g, "-")}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${item.accent === "pink" ? "bg-brand-pink-light" : "bg-brand-aqua-light"}`}>
                  <item.icon size={22} className={item.accent === "pink" ? "text-brand-pink" : "text-brand-aqua-dark"} />
                </div>
                <h3 className="font-heading text-xl font-bold text-brand-black mb-3">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </motion.div>
          <a href="#versions" className="mt-8 inline-flex items-center gap-1 text-brand-aqua-dark font-semibold text-sm hover:gap-2 transition-all" data-testid="register-industries-versions-link">
            See which version fits your business <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* Versions & pricing */}
      <section id="versions" className="py-20 md:py-28 bg-white" data-testid="register-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="max-w-2xl mb-10">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-3">Versions</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight mb-4">
              Lite, Core or Plus.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-brand-grey leading-relaxed">
              Per till, per month, excluding VAT. Free setup on every plan — the offer waives the £100 terminal setup fee.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start mb-10">
            {TIERS.map((tier) => (
              <motion.div key={tier.name} variants={fadeUp} className={`relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${tier.cardClass}`} data-testid={`register-tier-${tier.name.toLowerCase()}`}>
                {tier.badge && (
                  <span className={`absolute -top-3.5 right-6 ${tier.badgeClass} text-white text-xs font-bold rounded-full px-3.5 py-1`}>{tier.badge}</span>
                )}
                <h3 className="font-heading text-2xl font-black text-brand-black">{tier.name}</h3>
                <p className="text-sm font-semibold text-brand-grey mt-1 mb-4">{tier.desc}</p>
                <div className="flex items-baseline gap-2 mb-5">
                  <span className={`font-heading text-4xl font-black tracking-tight ${tier.accent === "pink" ? "text-brand-pink" : "text-brand-black"}`}>{tier.price}</span>
                  <span className="text-sm font-semibold text-brand-grey">/ month</span>
                </div>
                <ul className="space-y-2.5">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-brand-black">
                      <Check size={15} strokeWidth={3} className={`mt-0.5 flex-shrink-0 ${tier.accent === "pink" ? "text-brand-pink" : "text-brand-aqua-dark"}`} />{f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
            <motion.div variants={fadeUp} className="bg-brand-off-white border border-brand-light-grey rounded-2xl p-7 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-testid="register-extras">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua-dark mb-2">Add more tills</p>
                <p className="text-sm text-brand-grey leading-relaxed">Extra Core licence <strong className="text-brand-black font-bold">£19.99</strong>, extra Plus licence <strong className="text-brand-black font-bold">£29.99</strong>, A920 handheld <strong className="text-brand-black font-bold">£15.00</strong> — all per month.</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua-dark mb-2">Hardware</p>
                <p className="text-sm text-brand-grey leading-relaxed">PAX A920 Pro terminal kit <strong className="text-brand-black font-bold">£20.00</strong> a month, or <strong className="text-brand-black font-bold">£350</strong> to buy outright.</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-pink mb-2">Setup & training</p>
                <p className="text-sm text-brand-grey leading-relaxed">System build <strong className="text-brand-black font-bold">£300</strong>, or <strong className="text-brand-black font-bold">£500</strong> with installation and onsite training. The free-setup offer waives the £100 terminal setup fee — the system build is priced separately.</p>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4 mb-12">
              <a href="#contact" className="cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2" data-testid="register-cta-quote">
                Get a Quote <ArrowRight size={16} />
              </a>
              <p className="text-sm font-semibold text-brand-grey">Not sure which fits? We'll match you in one call.</p>
            </motion.div>
            <motion.div variants={fadeUp}>
              <SocialProofStrip reassurance="A real person answers — not a chatbot" testId="register-social-proof" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="register-faqs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="max-w-2xl mx-auto text-center mb-10">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-3">FAQs</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">
              Questions, answered.
            </motion.h2>
          </motion.div>
          <div className="max-w-3xl mx-auto">
            <ResponsiveAccordion items={FAQS} testIdPrefix="register-faq" />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative overflow-hidden bg-black py-16 md:py-20" data-testid="register-stats">
        <div className="absolute inset-0 pointer-events-none" style={STATS_OVERLAY_STYLE} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {STATS.map((stat) => (
              <div key={stat.figure}>
                <p className="stat-shine font-heading text-3xl sm:text-4xl font-black">{stat.figure}</p>
                <p className="text-sm text-brand-grey mt-1">{stat.caption}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA + lead form */}
      <section id="contact" className="cta-section-bg py-20 md:py-28" data-testid="register-contact">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-5">
                Let's get you trading.
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-8 max-w-xl">
                Book a free demo and we'll have Register set up — products, taxes and staff loaded — in days, not weeks. Setup is free on every plan.
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                <a href="tel:08003682989" className="bg-white text-brand-aqua-dark hover:bg-brand-aqua-light font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="register-cta-call">
                  <Phone size={16} /> Call 0800 368 2989
                </a>
                <a href="mailto:hello@candopayments.com" className="bg-white/15 border border-white/60 text-white hover:bg-white/25 font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="register-cta-email">
                  <Mail size={16} /> hello@candopayments.com
                </a>
              </div>
              <p className="text-sm font-semibold text-white/85 flex items-center gap-2">
                <CheckCircle size={16} className="flex-shrink-0" /> A real person answers — not a chatbot.
              </p>
              <div className="hidden lg:block mt-10">
                <FormReviewCompanion product="epos-systems" testId="register-form-review" />
              </div>
            </div>
            <div className="space-y-5">
              <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
                <h3 className="font-heading text-xl font-bold text-brand-black mb-4">Book a free Register demo</h3>
                <LeadForm schema={eposDemo} context={{ page: "Register" }} />
              </div>
              <div className="lg:hidden">
                <FormReviewCompanion product="epos-systems" testId="register-form-review-mobile" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <LocationCrossLinks />
      <RelatedReviews
        product="epos-systems"
        eyebrow="Customer Stories"
        heading="See what our customers say about our EPOS systems"
        subheading="Real feedback from UK businesses running their day on a CanDo EPOS."
        testId="register-related-reviews"
      />
    </div>
  );
}
